import { create } from 'zustand';
import { immer } from 'zustand/middleware/immer';

import { usePrintsUiStore } from './use-prints-ui-store';
import { usePrintsOrderStore } from './use-prints-order-store';
import ViewingSizeKey from '../mobile/viewing-size-key';

import jsonDataURL from '../common/json-data-url';
import SizeHelpers from '../common/size-helpers';
import {
  generateAvailableFinishesByDeliveryOption,
  determineInitialSelections,
} from '../utils/config-helpers';
import { UpdatePhotoEvents } from '../common/events/event-declarations';
import { selectAvailableSizesForDeliveryAndFinish } from './selectors';

export const usePrintsConfigStore = create(
  immer((set, get) => ({
    // --- STATE ---
    urls: {},
    allSizes: [],
    allFinishes: [],
    allDeliveryOptions: [],
    availableFinishesByDeliveryOption: {},
    features: {},

    // --- ACTIONS ---
    // Loads initial configuration data and basket contents to set up the prints builder.
    loadInitialData: async ({ urls, features }) => {
      const { setReady } = usePrintsUiStore.getState();
      const { loadBasket } = usePrintsOrderStore.getState();

      try {
        // 1. Load static configuration
        const configResponse = await fetch(jsonDataURL(urls.data));
        const configData = await configResponse.json();

        if (!configData.sizes.length || !configData.finishes.length) {
          console.error('No available finishes or sizes found.');
          // If no sizes/finishes, just mark ready and return. No modal needed.
          setReady(true); // App is ready, but empty
          return;
        }

        const availableFinishesByDeliveryOption =
          generateAvailableFinishesByDeliveryOption(
            configData.finishes,
            configData.sizes,
          );

        set((state) => {
          state.features = features;
          state.urls = urls;
          state.allSizes = configData.sizes;
          state.allFinishes = configData.finishes;
          state.allDeliveryOptions = configData.delivery_options;
          state.availableFinishesByDeliveryOption =
            availableFinishesByDeliveryOption;
        });

        // 2. Load basket/order first to see if we can derive initial selections from it
        await loadBasket(urls.basket_data);
        const { photos } = usePrintsOrderStore.getState();

        // 3. Determine initial selections based on basket contents or defaults
        get().applySelections(configData, photos);
      } catch (error) {
        console.error('Failed to load prints builder data:', error);
        setReady(true); // If any other error, still mark ready to unblock UI
      }
    },

    // Handles logic when the customer's cart is merged, updating selections.
    handleCartMerged: async () => {
      const { loadBasket } = usePrintsOrderStore.getState();
      const { allSizes, allFinishes, allDeliveryOptions, urls } = get();
      const { setReady } = usePrintsUiStore.getState();

      setReady(false);

      // 1. Load basket/order first to see if we can derive initial selections from it
      await loadBasket(urls.basket_data);
      const { photos } = usePrintsOrderStore.getState();

      const configData = {
        sizes: allSizes,
        finishes: allFinishes,
        delivery_options: allDeliveryOptions,
      };

      // 2. Determine initial selections based on basket contents or defaults
      get().applySelections(configData, photos);
    },

    // Private: Applies initial print selections based on config data and user photos.
    applySelections: (configData, photos) => {
      const { availableFinishesByDeliveryOption, features } = get();
      const { setInitialSelections, setPendingUiEvent, setReady } =
        usePrintsUiStore.getState();

      const selectionResult = determineInitialSelections(
        configData,
        availableFinishesByDeliveryOption,
        photos,
        features,
      );

      if (selectionResult.error) {
        const { nextDeliveryOption, printFinish } = selectionResult;

        const onContinue = () => {
          setInitialSelections({
            finishName: printFinish?.name,
            deliveryKey: nextDeliveryOption?.key,
            viewingSizeKey: SizeHelpers.ALL_PHOTOS_KEY,
          });
          setReady(true);
        };

        setPendingUiEvent({
          type: UpdatePhotoEvents.SHOW_INVALID_DELIVERY_OPTION_MODAL,
          payload: { nextDeliveryOption, printFinish, onContinue },
        });

        return;
      }

      const {
        initialFinish,
        initialDeliveryOption,
        viewingSizeKey,
        initialUploadSizeKey,
      } = selectionResult;
      const sizeKey = features.mobile
        ? viewingSizeKey
        : SizeHelpers.ALL_PHOTOS_KEY;
      setInitialSelections({
        finishName: initialFinish.name,
        deliveryKey: initialDeliveryOption.key,
        viewingSizeKey: sizeKey,
        initialUploadSizeKey: initialUploadSizeKey,
      });

      setReady(true);
    },

    // --- GETTERS ---
    getters: {
      getPrintSizeKeyForNewUpload: () => {
        const { allSizes } = get();
        const {
          selectedFinishName,
          selectedDeliveryKey,
          viewingSizeKey,
          selectedUploadSizeKey,
        } = usePrintsUiStore.getState();

        if (!allSizes || allSizes.length === 0) return null;

        // Use the mobile viewingSizeKey if it's a valid size key and matches the finish
        if (
          viewingSizeKey &&
          viewingSizeKey !== SizeHelpers.ALL_PHOTOS_KEY &&
          ViewingSizeKey.hasFinish(viewingSizeKey, selectedFinishName)
        ) {
          return viewingSizeKey;
        }

        // Next try the selectedUploadSizeKey (from URL or initial default)
        // Ensure it matches the currently selected finish name
        if (
          selectedUploadSizeKey &&
          ViewingSizeKey.hasFinish(selectedUploadSizeKey, selectedFinishName)
        ) {
          return selectedUploadSizeKey;
        }

        // Otherwise fallback to the default from available sizes for the current
        // finish and delivery option
        const availableSizes = selectAvailableSizesForDeliveryAndFinish(
          allSizes,
          selectedDeliveryKey,
          selectedFinishName,
        );

        return (
          SizeHelpers.defaultSize(availableSizes)?.key || availableSizes[0]?.key
        );
      },
    },
  })),
);
